import {
  json,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  redirect,
  type LinksFunction,
  type MetaFunction,
} from "@remix-run/node"
import { Link, useActionData, useLoaderData, useSearchParams, useSubmit } from "@remix-run/react"
import { useEffect, useRef } from "react"
import { withZod } from "@remix-validated-form/with-zod"
import { ValidatedForm, validationError } from "remix-validated-form"
import { z } from "zod"
import {
  AgeVerificationCheckbox,
  FormInput,
  FormPasswordInput,
  UsernameField,
  EmailTakenNotice,
  FormSelect,
  PASSWORD_HINT,
  SubmitButton,
} from "~/components/form-fields"
import Typography from "~/components/typography"
import { Button } from "~/components/ui/button"
import { Public } from "~/utils/remix"
import { usernameField } from "~/utils/username"
import { pruneUndefined } from "~/utils/data"
import { commitUserSession, createSession, register, getAnonSession, emailTakenKind } from "~/utils/session.server"
import { EventType, TrackEvent } from "~/models/events.server"
import { HeardAboutUs } from "~/utils/data"
import { EnqueueErosCredit, EnqueueClimaxCredit } from "~/workers/handlers.server"

export const meta: MetaFunction = () => {
  return [
    { title: "MakeLoveNotPorn - sign up to enjoy real world sex videos" },
    { property: "og:title", content: "MakeLoveNotPorn - sign up to enjoy real world sex videos" },
    {
      name: "description",
      content:
        "Ready to see what everybody else is really doing in bed? Sign up here for free to delve into the silly, loving real world sex available on MakeLoveNotPorn.",
    },
  ]
}

const validator = withZod(
  z.object({
    Email: z.string().email({ message: "Invalid email format" }).min(1, { message: "Email is required" }),
    UserName: usernameField(),
    Password: z.string().min(1, { message: "Password is required" }),
    HeardAboutUs: z.string().min(1, { message: "Heard about us is required" }),
    AgeVerification: z
      .string({ required_error: "You must confirm you are 18 or older to sign up" })
      .refine((val) => val === "on", { message: "You must confirm you are 18 or older to sign up" }),
    redirectTo: z.string().optional(),
    eros: z.string().optional(),
    climax: z.string().optional(),
    ref: z.string().optional(),
  })
)

export const action = Public(async ({ request, context }: ActionFunctionArgs) => {
  const formData = await request.formData()

  const secretKey = process.env.RECAPTCHA_SECRET_KEY
  if (secretKey) {
    const captchaToken = formData.get("g-recaptcha-response") as string | null
    if (!captchaToken) {
      return json({ captchaError: "Please complete the CAPTCHA." }, { status: 400 })
    }
    const verifyRes = await fetch(
      `https://www.google.com/recaptcha/api/siteverify?secret=${secretKey}&response=${captchaToken}`,
      { method: "POST" }
    )
    const verifyData = await verifyRes.json()
    if (!verifyData.success) {
      return json({ captchaError: "CAPTCHA verification failed. Please try again." }, { status: 400 })
    }
  }

  const form = await validator.validate(formData)
  if (form.error) {
    context.logger.warn(`Registration validation errors`, { form: form.data, error: form.error })
    return validationError(form.error)
  }

  const anonSession = await getAnonSession(request.headers.get("Cookie"))
  const anonSessionID = anonSession.get("id") ?? null

  const { user, errors, usernameSuggestions } = await register(
    context,
    form.data.UserName,
    form.data.Email,
    form.data.Password,
    undefined,
    anonSessionID
  )
  if (errors) {
    context.logger.warn(`Registration failed for ${form.data.UserName}`, { username: form.data.UserName, errors })

    const emailTaken = emailTakenKind(errors.errors.email)

    return json(
      {
        fieldErrors: pruneUndefined({
          Email: emailTaken ? undefined : errors.errors.email,
          UserName: errors.errors.username,
          Password: errors.errors.password,
        }),
        emailTaken,
        usernameSuggestions,
      },
      { status: errors.status }
    )
  }

  // If we have an invalid HeardAboutUs value, likely a bot. Disable account activation.
  if (!HeardAboutUs.map((x) => x.value).includes(form.data.HeardAboutUs)) {
    context.logger.warn(`Invalid HeardAboutUs value for user ${user.UserID}: ${form.data.HeardAboutUs}`, {
      data: form.data,
    })
    await context.db.users.update({
      where: { UserID: user.UserID },
      data: { Status: 2, FlaggedAsBot: new Date() },
    })
  }

  await context.db.users.update({
    where: { UserID: user.UserID },
    data: {
      RedirectToAfterActivation: form.data.redirectTo ?? (form.data.ref === "academy" ? "/subscribe" : undefined),
      IsTelehealthUser: form.data.ref === "academy" ? true : undefined,
    },
  })

  const session = await createSession()
  session.flash("success", {
    title: "We've successfully created your account!",
    message: "We've emailed you a confirmation link. Click it to activate your account.",
    additionalMsg:
      form.data.eros || form.data.climax ?
        "Once you're signed in, you'll find your free credit available in your account."
      : undefined,
    email: form.data.Email,
  })

  const signupIP = request.headers.get("x-forwarded-for")?.split(",")[0]?.trim()
    || request.headers.get("x-real-ip")
    || null

  if (signupIP) {
    await context.db.users.update({
      where: { UserID: user.UserID },
      data: { SignupIP: signupIP },
    })
  }

  context.user = user
  await TrackEvent(context, EventType.Signup, { HeardAboutUs: form.data.HeardAboutUs })

  if (form.data.eros) {
    await EnqueueErosCredit(context, { UserID: Number(user.UserID) })
  } else if (form.data.climax) {
    await EnqueueClimaxCredit(context, { UserID: Number(user.UserID) })
  }

  return redirect("/login", {
    headers: {
      "Set-Cookie": await commitUserSession(session),
    },
  })
})

export const loader = Public(async ({ request, context }: LoaderFunctionArgs) => {
  if (context.user) {
    return redirect("/")
  }

  return json({ recaptchaSiteKey: process.env.RECAPTCHA_SITE_KEY ?? null })
})

export const links: LinksFunction = () => [
  {
    rel: "canonical",
    href: "https://makelovenotporn.tv/register",
  },
]

export default function Register() {
  const actionData = useActionData<typeof action>()
  const { recaptchaSiteKey } = useLoaderData<typeof loader>()
  const suggestedUsernames =
    actionData && "usernameSuggestions" in actionData ? actionData.usernameSuggestions : []
  const emailTaken = actionData && "emailTaken" in actionData ? actionData.emailTaken : undefined
  const [searchParams] = useSearchParams()
  const submit = useSubmit()
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (!recaptchaSiteKey) return

    // Load reCAPTCHA script
    const script = document.createElement("script")
    script.src = "https://www.google.com/recaptcha/api.js"
    script.async = true
    script.defer = true
    document.head.appendChild(script)

    // Global callback — reCAPTCHA calls this after challenge completes
    ;(window as any).__onRecaptchaSuccess = () => {
      if (formRef.current) {
        submit(formRef.current, { method: "post" })
      }
    }

    return () => {
      try { document.head.removeChild(script) } catch {}
      delete (window as any).__onRecaptchaSuccess
    }
  }, [recaptchaSiteKey, submit])

  return (
    <>
      <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-5 lg:px-0">
        <Link
          to={`/login${searchParams.get("redirectTo") ? `?redirectTo=${searchParams.get("redirectTo")}` : ""}`}
          className="absolute right-4 top-4 md:right-8 md:top-8">
          <Button>Login</Button>
        </Link>
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex lg:col-span-3 dark:border-r">
          <div className="absolute inset-0 bg-zinc-900" />
          <div className="pl-8 pr-8 relative z-20 w-full h-full flex flex-col justify-center items-left">
            <Typography className="subpixel-antialiased text-zinc-200">
              <h1 className="text-4xl font-extralight tracking-tight text-pretty text-zinc-200 ">
                Ready to see what everybody else, all around the world, is <i className="font-black">really</i> doing in
                bed?
              </h1>
              <div className="p-4 font-thin tracking-wider text-xl text-pretty">
                <p>
                  <b>1.</b> &nbsp;&nbsp;Sign up for free
                </p>
                <p>
                  <b>2.</b> &nbsp;&nbsp;Browse thousands of videos, with new uploads every day
                </p>
                <p>
                  <b>3.</b> &nbsp;&nbsp;Subscribe or buy credits to rent and be inspired by real world sex
                </p>
                <p>
                  <b>4.</b> &nbsp;&nbsp;Reward MakeLoveNotPornstars who get half of each rental
                </p>
              </div>
            </Typography>
          </div>
        </div>
        <div className="lg:p-8 lg:h-screen flex justify-center lg:col-span-2">
          <div className="mx-auto flex w-full flex-col justify-center space-y-1 lg:space-y-6 sm:w-[350px]">
            <div className="flex flex-col space-y-2 text-center lg:hidden mt-20">
              <h1 className="text-md font-semibold tracking-tight">
                Ready to see what everybody else, all around the world, is <i className="font-black">really</i> doing in
                bed?
              </h1>
              <div className="p-2 pl-4 tracking-wider text-xs text-pretty flex flex-col gap-1 text-left">
                <p>
                  <b>1.</b> &nbsp;&nbsp;Sign up for free
                </p>
                <p>
                  <b>2.</b> &nbsp;&nbsp;Browse thousands of videos, with new uploads every day
                </p>
                <p>
                  <b>3.</b> &nbsp;&nbsp;Subscribe or buy credits to rent and be inspired by real world sex
                </p>
                <p>
                  <b>4.</b> &nbsp;&nbsp;Reward MakeLoveNotPornstars who get half of each rental
                </p>
              </div>
              <h1 className="text-md font-semibold tracking-tight">Create Your Account</h1>
            </div>
            <div className="space-y-2 text-center hidden lg:flex lg:flex-col">
              <h1 className="text-2xl font-semibold tracking-tight">Create Your Account</h1>
              <p className="text-sm text-muted-foreground">Free to join. Subscribe anytime to start watching.</p>
            </div>
            {searchParams.get("eros") && (
              <div className="space-y-2 text-center flex flex-col bg-primary text-primary-foreground rounded-lg p-2">
                <p>
                  Join now to receive <b>1 free credit</b> for Real World Sex on Eros!
                </p>
              </div>
            )}
            {searchParams.get("climax") && (
              <div className="space-y-2 text-center flex flex-col bg-primary text-primary-foreground rounded-lg p-2">
                <p>
                  Join now to receive <b>1 free credit</b> for Real World Sex on Climax!
                </p>
              </div>
            )}
            <ValidatedForm
              validator={validator}
              method="post"
              formRef={formRef}
              onSubmit={(_, e) => {
                if (!recaptchaSiteKey) return
                e.preventDefault()
                ;(window as any).grecaptcha?.execute()
              }}
            >
              <input
                type="hidden"
                name="redirectTo"
                value={searchParams.get("redirectTo") ? searchParams.get("redirectTo")! : undefined}
              />
              <input
                type="hidden"
                name="eros"
                value={searchParams.get("eros") ? searchParams.get("eros")! : undefined}
              />
              <input
                type="hidden"
                name="climax"
                value={searchParams.get("climax") ? searchParams.get("climax")! : undefined}
              />
              <input
                type="hidden"
                name="ref"
                value={searchParams.get("ref") ? searchParams.get("ref")! : undefined}
              />
              {recaptchaSiteKey && (
                <div
                  className="g-recaptcha"
                  data-sitekey={recaptchaSiteKey}
                  data-callback="__onRecaptchaSuccess"
                  data-size="invisible"
                />
              )}
              <div className="grid gap-4">
                <FormInput type="email" name="Email" label="Email Address" autoComplete="email">
                  {emailTaken && <EmailTakenNotice kind={emailTaken} />}
                </FormInput>
                <UsernameField serverSuggestions={suggestedUsernames} />
                <FormPasswordInput
                  name="Password"
                  label="Password"
                  hint={PASSWORD_HINT}
                  autoComplete="new-password"
                />
                <FormSelect
                  name="HeardAboutUs"
                  label="How did you hear about us?"
                  placeholder="Select an option"
                  options={HeardAboutUs}
                />
                <AgeVerificationCheckbox />
                {actionData && "captchaError" in actionData && (
                  <p className="text-sm font-medium text-red-600 text-center">{actionData.captchaError}</p>
                )}
                <SubmitButton content="Let’s do this!" loading="Doing it..." />
              </div>
            </ValidatedForm>
            <Typography className="px-8 text-center text-sm text-muted-foreground">
              By creating an account, you agree to our <Link to="/terms-of-service-agreement">Terms of Service</Link>{" "}
              and <Link to="/privacy-policy">Privacy Policy</Link>.
            </Typography>
          </div>
        </div>
      </div>
    </>
  )
}
